import { useState, useCallback, useEffect } from 'react';
import { audioEngine, PlaybackState } from '@/core/AudioEngine';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import { realtimeAudioConverter } from '@/core/RealtimeAudioConverter';
import { debugLogger } from '@/utils/debugLogger';

interface UseAudioPlaybackProps {
  audioId: string;
  mediaUrl: string;
  audioDuration?: number;
}

/**
 * Validate and sanitize audio duration for playback
 */
const validatePlaybackDuration = (duration: number | undefined | null): number => {
  // Handle undefined/null cases
  if (duration === undefined || duration === null) {
    return 0;
  }
  
  // Convert to number if it's a string
  const numDuration = typeof duration === 'string' ? parseFloat(duration) : duration;
  
  // Validate the number
  if (!Number.isFinite(numDuration) || numDuration < 0 || isNaN(numDuration)) {
    debugLogger.warn('useAudioPlayback', 'Invalid duration for playback:', { 
      original: duration, 
      converted: numDuration,
      type: typeof duration 
    });
    return 0;
  }
  
  // Ensure duration is within reasonable bounds
  const maxDuration = 600; // 10 minutes in seconds
  if (numDuration > maxDuration) {
    debugLogger.warn('useAudioPlayback', 'Duration exceeds maximum for playback:', { 
      duration: numDuration, 
      maxDuration 
    });
    return maxDuration;
  }
  
  return Math.round(numDuration * 100) / 100; // Round to 2 decimal places
};

export const useAudioPlayback = ({ audioId, mediaUrl, audioDuration = 0 }: UseAudioPlaybackProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  
  // Initialize duration with validated value from database
  const [duration, setDuration] = useState(() => {
    const validatedDuration = validatePlaybackDuration(audioDuration);
    debugLogger.debug('useAudioPlayback', 'Initializing with duration from database:', {
      audioId,
      originalDuration: audioDuration,
      validatedDuration
    });
    return validatedDuration;
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [hasTriedConversion, setHasTriedConversion] = useState(false);

  // Get signed URL for private audio
  const { signedUrl, isLoading: urlLoading, error: urlError } = useSignedMediaUrl({
    mediaUrl,
    contentType: 'audio'
  });

  // Update duration when audioDuration prop changes
  useEffect(() => {
    const validDuration = validatePlaybackDuration(audioDuration);
    if (validDuration > 0) {
      debugLogger.debug('useAudioPlayback', 'Updating duration from prop change:', {
        audioId,
        newDuration: validDuration,
        previousDuration: duration
      });
      setDuration(validDuration);
    }
  }, [audioDuration, audioId, duration]);

  // Update playback state from AudioEngine
  useEffect(() => {
    const updateState = () => {
      const state = audioEngine.getPlaybackState(audioId);
      if (state) {
        setIsPlaying(state.isPlaying);
        setCurrentTime(validatePlaybackDuration(state.audio.currentTime));
        
        // Only use audio element duration if we don't have a valid duration from database
        const audioDur = validatePlaybackDuration(state.duration);
        const currentValidDuration = validatePlaybackDuration(duration);
        
        if (audioDur > 0 && currentValidDuration === 0) {
          debugLogger.debug('useAudioPlayback', 'Using audio element duration as fallback:', {
            audioId,
            audioDuration: audioDur,
            databaseDuration: currentValidDuration
          });
          setDuration(audioDur);
        }
      }
    };

    const interval = setInterval(updateState, 100);
    return () => clearInterval(interval);
  }, [audioId, duration]);

  // Initialize AudioEngine callbacks
  useEffect(() => {
    audioEngine.initialize({
      userId: 'current-user',
      onPlaybackStart: (id) => {
        if (id === audioId) {
          setIsPlaying(true);
          setError(null);
          setIsConverting(false);
        }
      },
      onPlaybackEnd: (id) => {
        if (id === audioId) {
          setIsPlaying(false);
          setCurrentTime(0);
        }
      },
      onPlaybackError: (id, error) => {
        if (id === audioId) {
          debugLogger.error('useAudioPlayback', `Playback error for ${id}:`, error);
          
          // Only try conversion if we haven't already tried
          if (!hasTriedConversion && realtimeAudioConverter.needsConversion()) {
            handlePlaybackError(error.message);
          } else {
            setError('Erro ao reproduzir áudio');
            setIsPlaying(false);
          }
        }
      }
    });
  }, [audioId, hasTriedConversion]);

  // Reset conversion state when URL changes
  useEffect(() => {
    setHasTriedConversion(false);
    setError(null);
    setIsConverting(false);
    setConversionProgress(0);
    
    // Update duration when audioDuration prop changes
    const validDuration = validatePlaybackDuration(audioDuration);
    if (validDuration > 0) {
      setDuration(validDuration);
    }
  }, [signedUrl, audioDuration]);

  // Handle playback errors with conversion (only once per URL)
  const handlePlaybackError = async (errorMessage: string) => {
    if (hasTriedConversion) {
      debugLogger.warn('useAudioPlayback', 'Conversion already attempted, skipping');
      setError('Erro ao reproduzir áudio após conversão');
      setIsPlaying(false);
      return;
    }

    debugLogger.debug('useAudioPlayback', 'Handling playback error, attempting conversion...');
    
    if (!signedUrl) {
      setError('URL de áudio não disponível');
      setIsPlaying(false);
      return;
    }

    setHasTriedConversion(true);

    try {
      setIsConverting(true);
      setConversionProgress(0);
      setError(null);

      debugLogger.debug('useAudioPlayback', 'Starting conversion for:', audioId);

      // Convert audio to compatible format
      const convertedUrl = await realtimeAudioConverter.convertAudio(
        signedUrl,
        (progress) => {
          setConversionProgress(progress);
        }
      );

      debugLogger.debug('useAudioPlayback', 'Conversion completed, starting playback with converted URL');

      // Try playing with converted URL
      const success = await audioEngine.startPlayback(audioId, convertedUrl);
      
      if (!success) {
        throw new Error('Falha ao reproduzir áudio convertido');
      }

      setIsConverting(false);
      setConversionProgress(0);

    } catch (conversionError) {
      debugLogger.error('useAudioPlayback', 'Conversion failed:', conversionError);
      setError(`Erro na conversão: ${conversionError instanceof Error ? conversionError.message : 'Erro desconhecido'}`);
      setIsPlaying(false);
      setIsConverting(false);
      setConversionProgress(0);
    }
  };

  const play = useCallback(async () => {
    if (!signedUrl) {
      setError('URL de áudio não disponível');
      return;
    }

    debugLogger.debug('useAudioPlayback', 'Starting playback with duration:', {
      audioId,
      duration,
      signedUrl: signedUrl.substring(0, 50) + '...'
    });

    setIsLoading(true);
    setError(null);

    try {
      // For iOS/Safari, validate playback capability first (only if not already tried)
      if (realtimeAudioConverter.needsConversion() && !hasTriedConversion) {
        debugLogger.debug('useAudioPlayback', 'Validating audio playback for iOS/Safari...');
        
        const canPlay = await realtimeAudioConverter.validateAudioPlayback(signedUrl);
        
        if (!canPlay) {
          debugLogger.debug('useAudioPlayback', 'Audio validation failed, starting conversion...');
          setHasTriedConversion(true);
          await handlePlaybackError('Format not supported');
          return;
        }
      }

      // Try normal playback first
      const success = await audioEngine.startPlayback(audioId, signedUrl);
      if (!success) {
        setError('Falha ao reproduzir áudio');
      }
    } catch (error) {
      debugLogger.error('useAudioPlayback', 'Playback initiation failed:', error);
      setError('Erro ao reproduzir áudio');
    } finally {
      setIsLoading(false);
    }
  }, [audioId, signedUrl, hasTriedConversion, duration]);

  const pause = useCallback(() => {
    audioEngine.pausePlayback(audioId);
    setIsPlaying(false);
  }, [audioId]);

  const resume = useCallback(() => {
    audioEngine.resumePlayback(audioId);
    setIsPlaying(true);
  }, [audioId]);

  const stop = useCallback(() => {
    audioEngine.stopPlayback(audioId);
    setIsPlaying(false);
    setCurrentTime(0);
  }, [audioId]);

  const seekTo = useCallback((time: number) => {
    const validTime = validatePlaybackDuration(time);
    audioEngine.seekTo(audioId, validTime);
    setCurrentTime(validTime);
  }, [audioId]);

  const toggle = useCallback(() => {
    if (isConverting) {
      // Don't allow toggle during conversion
      return;
    }

    if (isPlaying) {
      pause();
    } else if (currentTime > 0) {
      resume();
    } else {
      play();
    }
  }, [isPlaying, currentTime, isConverting, play, pause, resume]);

  return {
    isPlaying,
    currentTime,
    duration,
    isLoading: isLoading || urlLoading,
    error: error || urlError,
    hasSignedUrl: !!signedUrl,
    isConverting,
    conversionProgress,
    play,
    pause,
    resume,
    stop,
    seekTo,
    toggle
  };
};
